import { Container, Graphics } from 'pixi.js';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
  alpha: number;
  gravity: number;
  friction: number;
  /** Peak alpha at full life (default 0.8). */
  baseAlpha?: number;
  /** Size growth per second (e.g. an expanding shockwave). */
  grow?: number;
  /** Render as a stroked ring instead of a filled disc. */
  ring?: boolean;
  ringWidth?: number;
}

export class ParticleSystem {
  private container: Container;
  private particles: Particle[] = [];
  private graphics: Graphics;

  constructor(parentContainer: Container) {
    this.container = new Container();
    this.graphics = new Graphics();
    this.container.addChild(this.graphics);
    parentContainer.addChild(this.container);
  }

  emitSplash(x: number, y: number, count: number = 20): void {
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
      const speed = 100 + Math.random() * 200;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.5 + Math.random() * 0.5,
        maxLife: 1,
        size: 2 + Math.random() * 4,
        color: 0x4169e1,
        alpha: 0.8,
        gravity: 400,
        friction: 0.98,
      });
    }
  }

  emitDebris(x: number, y: number, count: number = 15, color: number = 0x8b4513): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 50 + Math.random() * 150;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 100,
        life: 0.8 + Math.random() * 0.7,
        maxLife: 1.5,
        size: 2 + Math.random() * 3,
        color,
        alpha: 1,
        gravity: 500,
        friction: 0.95,
      });
    }
  }

  emitExplosion(x: number, y: number, radius: number): void {
    const rnd = Math.random;
    const fireCount = Math.max(10, Math.floor(radius));

    // Expanding shockwave ring.
    this.particles.push({
      x,
      y,
      vx: 0,
      vy: 0,
      life: 0.34,
      maxLife: 0.34,
      size: radius * 0.35,
      color: 0xffe9a8,
      alpha: 1,
      baseAlpha: 0.7,
      gravity: 0,
      friction: 1,
      grow: radius * 3.2,
      ring: true,
      ringWidth: 3,
    });

    // Bright flash core.
    for (let i = 0; i < 5; i++) {
      this.particles.push({
        x: x + (rnd() - 0.5) * 8,
        y: y + (rnd() - 0.5) * 8,
        vx: (rnd() - 0.5) * 40,
        vy: (rnd() - 0.5) * 40,
        life: 0.1 + rnd() * 0.1,
        maxLife: 0.2,
        size: radius * 0.45 * (0.5 + rnd() * 0.6),
        color: rnd() > 0.5 ? 0xffffff : 0xfff0a0,
        alpha: 1,
        baseAlpha: 1,
        gravity: 0,
        friction: 0.9,
      });
    }

    // Fireball: yellow -> orange -> deep red.
    for (let i = 0; i < fireCount; i++) {
      const angle = rnd() * Math.PI * 2;
      const speed = 60 + rnd() * radius * 2.2;
      const c = rnd() < 0.4 ? 0xffcc33 : rnd() < 0.8 ? 0xff7a1a : 0xff3b10;
      this.particles.push({
        x: x + (rnd() - 0.5) * 10,
        y: y + (rnd() - 0.5) * 10,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 40,
        life: 0.3 + rnd() * 0.45,
        maxLife: 0.75,
        size: 3 + rnd() * 6,
        color: c,
        alpha: 1,
        baseAlpha: 0.95,
        gravity: 120,
        friction: 0.9,
      });
    }

    // Fast bright sparks.
    const sparks = Math.floor(radius * 0.6);
    for (let i = 0; i < sparks; i++) {
      const angle = rnd() * Math.PI * 2;
      const speed = 130 + rnd() * radius * 3;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.25 + rnd() * 0.3,
        maxLife: 0.55,
        size: 1 + rnd() * 1.6,
        color: 0xffe06a,
        alpha: 1,
        baseAlpha: 1,
        gravity: 220,
        friction: 0.95,
      });
    }

    this.emitSmoke(x, y, Math.floor(radius * 0.4));
    this.emitDebris(x, y, Math.floor(radius * 0.6), 0x5a3a18);
  }

  emitSmoke(x: number, y: number, count: number = 10): void {
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.5;
      const speed = 20 + Math.random() * 40;

      this.particles.push({
        x: x + (Math.random() - 0.5) * 20,
        y: y + (Math.random() - 0.5) * 20,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1 + Math.random() * 1,
        maxLife: 2,
        size: 8 + Math.random() * 12,
        color: 0x555555,
        alpha: 0.5,
        gravity: -20,
        friction: 0.98,
      });
    }
  }

  /** Bright burst + smoke at the muzzle when a shot is fired (angle = launch dir). */
  emitMuzzleFlash(x: number, y: number, angle: number): void {
    for (let i = 0; i < 5; i++) {
      const sp = 50 + Math.random() * 70;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * sp,
        vy: Math.sin(angle) * sp,
        life: 0.07 + Math.random() * 0.08,
        maxLife: 0.15,
        size: 5 + Math.random() * 5,
        color: Math.random() > 0.5 ? 0xffffff : 0xffe08a,
        alpha: 1,
        baseAlpha: 1,
        gravity: 0,
        friction: 0.85,
      });
    }
    for (let i = 0; i < 4; i++) {
      const a = angle + (Math.random() - 0.5) * 0.8;
      const sp = 30 + Math.random() * 40;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 10,
        life: 0.25 + Math.random() * 0.2,
        maxLife: 0.45,
        size: 3 + Math.random() * 4,
        color: 0xb4b4b4,
        alpha: 1,
        baseAlpha: 0.4,
        gravity: 20,
        friction: 0.9,
        grow: 5,
      });
    }
  }

  /** A single soft smoke puff left behind a flying projectile. */
  emitTrail(x: number, y: number, color: number = 0x9a9a9a, size: number = 4): void {
    this.particles.push({
      x: x + (Math.random() - 0.5) * 3,
      y: y + (Math.random() - 0.5) * 3,
      vx: (Math.random() - 0.5) * 16,
      vy: (Math.random() - 0.5) * 16 - 8,
      life: 0.3 + Math.random() * 0.3,
      maxLife: 0.6,
      size: size * (0.7 + Math.random() * 0.5),
      color,
      alpha: 1,
      baseAlpha: 0.4,
      gravity: -10,
      friction: 0.92,
      grow: 6,
    });
  }

  emitChargeTrail(x: number, y: number): void {
    for (let i = 0; i < 2; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 6,
        y: y + (Math.random() - 0.5) * 6,
        vx: (Math.random() - 0.5) * 20,
        vy: -30 - Math.random() * 20,
        life: 0.3 + Math.random() * 0.2,
        maxLife: 0.5,
        size: 2 + Math.random() * 3,
        color: 0xffff00,
        alpha: 0.8,
        gravity: -50,
        friction: 0.95,
      });
    }
  }

  update(deltaTime: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      p.vy += p.gravity * deltaTime;
      p.vx *= p.friction;
      p.vy *= p.friction;

      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;

      if (p.grow) p.size = Math.max(0, p.size + p.grow * deltaTime);

      p.life -= deltaTime;
      p.alpha = Math.max(0, p.life / p.maxLife) * (p.baseAlpha ?? 0.8);

      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
  }

  render(): void {
    this.graphics.clear();

    for (const p of this.particles) {
      if (p.ring) {
        this.graphics
          .circle(p.x, p.y, p.size)
          .stroke({ color: p.color, alpha: p.alpha, width: p.ringWidth ?? 3 });
      } else {
        this.graphics.circle(p.x, p.y, p.size).fill({ color: p.color, alpha: p.alpha });
      }
    }
  }

  clear(): void {
    this.particles = [];
    this.graphics.clear();
  }

  getParticleCount(): number {
    return this.particles.length;
  }
}
